import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MoonIcon, SunIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";

const NavbarRetroDual = () => {
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationLinks = [
    { path: "/", label: "Home", icon: "🏠", color: "bg-blue-400 dark:bg-cyan-400" },
    { path: "/02_about", label: "About", icon: "👤", color: "bg-green-400" },
    { path: "/03_work-experience", label: "Experience", icon: "💼", color: "bg-orange-400 dark:bg-pink-400" },
    { path: "/04_projects", label: "Projects", icon: "🔧", color: "bg-purple-400" },
    { path: "/05_contact", label: "Contact", icon: "📧", color: "bg-yellow-400" },
  ];

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full z-50 bg-[#FFF8E7] dark:bg-[#2d2d44] border-b-4 border-black dark:border-cyan-400 shadow-[0px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0px_4px_0px_0px_rgba(0,255,255,0.5)] transition-all duration-500">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            
            {/* Logo/Brand - Retro Window Style */}
            <Link 
              to="/"
              className="flex items-center gap-2 group"
              onClick={closeMobileMenu}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 dark:from-pink-500 dark:to-purple-600 border-3 border-black dark:border-cyan-400 flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] group-hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:group-hover:shadow-[0_0_20px_rgba(0,255,255,0.7)] transition-all duration-500">
                <span className="text-white font-black text-lg">CM</span>
              </div>
              <div className="hidden sm:block">
                <span className="font-black text-xl uppercase tracking-tight group-hover:text-blue-600 dark:group-hover:text-cyan-400 transition-colors duration-300">
                  CONNOR MADIGAN
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Chunky Buttons */}
            <div className="hidden md:flex items-center gap-2">
              {navigationLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-4 py-2 border-3 border-black dark:border-cyan-400 font-black uppercase text-sm tracking-tight transition-all duration-500 ${
                      isActive
                        ? `${link.color} shadow-[inset_2px_2px_0px_0px_rgba(0,0,0,0.3)] dark:shadow-[0_0_15px_currentColor]`
                        : "bg-white dark:bg-[#1a1a2e] hover:bg-gray-100 dark:hover:bg-cyan-400 dark:hover:text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(0,255,255,0.5)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_15px_rgba(0,255,255,0.7)] hover:translate-x-[-1px] hover:translate-y-[-1px] dark:text-cyan-400"
                    }`}
                  >
                    <span className="mr-1">{link.icon}</span>
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Right Side Controls - Retro Buttons */}
            <div className="flex items-center gap-2">
              
              {/* Dark Mode Toggle - Retro Style */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="w-10 h-10 bg-yellow-300 dark:bg-purple-500 border-3 border-black dark:border-cyan-400 hover:bg-yellow-400 dark:hover:bg-purple-600 transition-all flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.5)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_20px_rgba(138,43,226,0.7)] hover:translate-x-[-1px] hover:translate-y-[-1px] duration-500"
                aria-label="Toggle dark mode"
              >
                {darkMode ? (
                  <SunIcon className="w-6 h-6 text-yellow-300 drop-shadow-[0_0_10px_rgba(255,255,0,0.8)]" />
                ) : (
                  <MoonIcon className="w-6 h-6 text-gray-800" />
                )}
              </button>

              {/* Mobile Menu Button - Retro Style */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden w-10 h-10 bg-blue-400 dark:bg-cyan-400 border-3 border-black dark:border-cyan-400 hover:bg-blue-500 dark:hover:bg-cyan-500 transition-all flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_20px_rgba(0,255,255,0.7)] hover:translate-x-[-1px] hover:translate-y-[-1px] duration-500"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6 text-black font-black" />
                ) : (
                  <Bars3Icon className="w-6 h-6 text-black font-black" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Retro Dropdown Window */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div 
            className="absolute inset-0 bg-black/30 dark:bg-black/60 transition-all duration-300"
            onClick={closeMobileMenu}
          ></div>
          
          {/* Mobile Menu Panel - Window Style */}
          <div className="absolute top-16 left-0 right-0 bg-[#FFF8E7] dark:bg-[#2d2d44] border-b-4 border-x-4 border-black dark:border-cyan-400 shadow-[0px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0px_8px_0px_0px_rgba(0,255,255,0.5)] transition-all duration-500">
            {/* Window Title Bar */}
            <div className="bg-gradient-to-r from-blue-400 to-purple-500 dark:from-cyan-400 dark:to-purple-600 border-b-4 border-black dark:border-cyan-400 px-4 py-2 flex items-center justify-between transition-all duration-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500 border-2 border-black dark:shadow-[0_0_10px_rgba(255,0,0,0.8)]"></div>
                <span className="font-black uppercase text-sm tracking-tight text-black dark:text-cyan-300 transition-colors duration-500">NAVIGATION</span>
              </div>
              <button 
                onClick={closeMobileMenu}
                className="w-6 h-6 bg-white dark:bg-[#1a1a2e] border-2 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-gray-200 dark:hover:bg-cyan-400 dark:hover:text-black dark:text-cyan-300 transition-all duration-300"
              >
                ✕
              </button>
            </div>

            {/* Menu Items */}
            <div className="p-4 space-y-2">
              {navigationLinks.map((link, index) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={closeMobileMenu}
                    className={`block px-4 py-3 border-3 border-black dark:border-cyan-400 font-black uppercase text-sm transition-all duration-500 ${
                      isActive
                        ? `${link.color} shadow-[inset_2px_2px_0px_0px_rgba(0,0,0,0.3)] dark:shadow-[0_0_15px_currentColor]`
                        : "bg-white dark:bg-[#1a1a2e] hover:bg-gray-100 dark:hover:bg-cyan-400 dark:hover:text-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(0,255,255,0.5)] dark:text-cyan-300"
                    }`}
                    style={{
                      animationDelay: `${index * 50}ms`,
                      animation: isMobileMenuOpen ? 'slideDown 0.2s ease-out forwards' : 'none'
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span>
                        <span className="mr-2 text-lg">{link.icon}</span>
                        {link.label}
                      </span>
                      {isActive && (
                        <div className="w-3 h-3 bg-black dark:bg-cyan-400 rounded-full dark:shadow-[0_0_10px_rgba(0,255,255,0.8)] transition-all duration-500"></div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Fun Retro Footer */}
            <div className="border-t-4 border-black dark:border-cyan-400 bg-gray-200 dark:bg-[#1a1a2e] px-4 py-2 flex items-center justify-between transition-all duration-500">
              <span className="text-xs font-bold dark:text-cyan-400 transition-colors duration-500">
                STATUS: {darkMode ? 'NIGHT MODE' : 'READY'}
              </span>
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-green-500 dark:bg-cyan-400 border border-black dark:border-cyan-400 rounded-full animate-pulse dark:shadow-[0_0_8px_rgba(0,255,255,0.8)]"></div>
                <div className="w-2 h-2 bg-green-500 dark:bg-pink-400 border border-black dark:border-pink-400 rounded-full animate-pulse dark:shadow-[0_0_8px_rgba(255,0,255,0.8)]" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-green-500 dark:bg-yellow-400 border border-black dark:border-yellow-400 rounded-full animate-pulse dark:shadow-[0_0_8px_rgba(255,255,0,0.8)]" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add custom CSS for animations */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default NavbarRetroDual;